"use client";

import { Suspense, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, ShieldCheck } from "lucide-react";
import Header from "@/components/header";
import Footer from "@/components/footer";

function AdminLoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/admin/create-blog";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error || "Login failed.");
        return;
      }

      navigate(redirectTo);
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Header />
      <main className="min-h-screen flex items-center justify-center bg-bg-soft px-4 pt-28 pb-12">
        <div className="w-full max-w-[430px] bg-bg-main p-8 sm:p-10 rounded-[2rem] border border-border-light shadow-[0_20px_50px_rgba(217,31,38,0.12)]">
          <div className="mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-red/10 text-accent-red text-xs font-bold uppercase tracking-wide">
              <ShieldCheck className="w-4 h-4" /> Admin Access
            </span>
            <h1 className="mt-5 mb-2 text-3xl sm:text-4xl font-semibold text-charcoal">
              Blog Admin Login
            </h1>
            <p className="text-muted text-sm">Sign in to access the blog creation panel.</p>
          </div>

          {error && (
            <div className="mb-5 rounded-2xl border border-accent-red/20 bg-accent-red/5 text-accent-red text-sm px-4 py-3" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-accent-red ml-1">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-6 py-4 bg-bg-soft rounded-2xl focus:ring-4 focus:ring-accent-red/20 outline-none transition-all text-charcoal placeholder:text-muted"
                placeholder="admin@example.com"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-accent-red ml-1">Password</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-6 py-4 bg-bg-soft rounded-2xl focus:ring-4 focus:ring-accent-red/20 outline-none transition-all text-charcoal placeholder:text-muted"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-accent-red hover:bg-red-700 disabled:opacity-60 text-white font-bold text-base rounded-2xl shadow-lg shadow-accent-red/30 transition-all flex items-center justify-center gap-2"
            >
              {loading ? "Signing in…" : "Sign In"}
              {!loading && <LogIn className="w-4 h-4" />}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default function AdminLoginPage() {
  return (
    <Suspense
      fallback={
        <main className="flex min-h-screen items-center justify-center bg-bg-soft">
          <p className="text-sm font-semibold text-muted">Loading…</p>
        </main>
      }
    >
      <AdminLoginForm />
    </Suspense>
  );
}
